/**
 * SD Animation Library (WEB-375)
 *
 * Drives the scroll-triggered and page-load reveal effects. Parallax and the
 * card hover glow are CSS-only and need nothing here.
 *
 * Why JavaScript at all: CSS `animation-timeline: view()` is scrub-linked, so it
 * reverses on scroll-up and replays on every re-entry, and its progress follows
 * scroll position rather than a clock. The spec calls for a fixed-duration run
 * that fires once. `animation-trigger: view() once` does exactly that but is not
 * in the support floor, so an IntersectionObserver covers it instead.
 *
 * The hidden start state lives behind [data-sd-anim-ready], set here. If this
 * script never runs, nothing is hidden and every section renders statically.
 */

import { mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';

const REVEAL_THRESHOLD = 0.6;
const READY_ATTRIBUTE = 'data-sd-anim-ready';
const VISIBLE_CLASS = 'sd-anim--in';
const TRIGGER_ATTRIBUTE = 'data-sd-anim-trigger';
const MAX_TARGET_DEPTH = 3;

/**
 * Hosts that fetch and swap in their own markup after load. They dispatch no
 * event when they do, so the only way to notice is to watch them — but watching
 * just these two is far cheaper than watching the whole document, and they are
 * present on most pages whether or not anything is animated.
 *
 * They are also off limits as reveal targets. Horizon's <product-recommendations>
 * reloads itself on any attribute or class change to its own element, and a
 * second load aborts the first, whose rejected fetch hides the section for good.
 * The reveal therefore always goes on a wrapper inside one of these, never on
 * the host — the Liquid places the settings there, and the two guards below stop
 * anything from drifting back onto it.
 */
const ASYNC_HOSTS = 'sd-cart-recommendations, product-recommendations';

/**
 * The element whose children actually animate. Several sections wrap their real
 * content in a single component or container, and animating a lone child means
 * the stagger delay has nothing to stagger. Descending past those wrappers puts
 * the reveal on the first element that has siblings to sequence.
 *
 * The settings stay on the original element; custom properties inherit down.
 *
 * @param {Element} element
 * @returns {Element}
 */
function resolveTarget(element) {
  let target = element;

  for (let depth = 0; depth < MAX_TARGET_DEPTH; depth++) {
    if (target.children.length !== 1) break;
    if (target.children[0].matches(ASYNC_HOSTS)) break;
    target = target.children[0];
  }

  return target;
}

/** @type {IntersectionObserver | undefined} */
let observer;

/**
 * @param {Element} element
 */
function reveal(element) {
  element.classList.add(VISIBLE_CLASS);
}

/**
 * @param {IntersectionObserverEntry[]} entries
 * @param {IntersectionObserver} instance
 */
function onIntersect(entries, instance) {
  for (const entry of entries) {
    if (!entry.isIntersecting) {
      // A repeating target resets on the way out so it can play again on return.
      if (entry.target.getAttribute(TRIGGER_ATTRIBUTE) === 'repeat') {
        entry.target.classList.remove(VISIBLE_CLASS);
      }
      continue;
    }

    // A section taller than the viewport can never reach a 0.6 ratio, so filling
    // 60% of the viewport counts as the same trigger point.
    const fillsViewport = entry.boundingClientRect.height > window.innerHeight * REVEAL_THRESHOLD;
    if (entry.intersectionRatio < REVEAL_THRESHOLD && !fillsViewport) continue;

    reveal(entry.target);

    // Fires once unless the editor asked for it every time.
    if (entry.target.getAttribute(TRIGGER_ATTRIBUTE) !== 'repeat') {
      instance.unobserve(entry.target);
    }
  }
}

/**
 * Claims every un-claimed reveal wrapper inside `root`. Safe to call repeatedly —
 * the ready attribute marks what has already been handled, which is what lets
 * theme-editor reloads and async section swaps re-run this.
 *
 * @param {ParentNode} root
 */
function claim(root) {
  const elements = root.querySelectorAll('[data-sd-anim]');
  if (elements.length === 0) return;

  observer ??= new IntersectionObserver(onIntersect, {
    threshold: [0, 0.25, REVEAL_THRESHOLD],
  });

  for (const element of elements) {
    const target = resolveTarget(element);
    // Writing to one of these hides it (see ASYNC_HOSTS). Skipping costs the
    // animation on that one element; writing costs the whole section.
    if (target.matches(ASYNC_HOSTS)) continue;
    if (target.hasAttribute(READY_ATTRIBUTE)) continue;

    target.setAttribute(READY_ATTRIBUTE, '');
    const trigger = element.getAttribute(TRIGGER_ATTRIBUTE);
    if (trigger) target.setAttribute(TRIGGER_ATTRIBUTE, trigger);
    startWatching();

    if (element.getAttribute(TRIGGER_ATTRIBUTE) === 'load') {
      // One frame so the hidden start state is painted before the animation runs.
      requestAnimationFrame(() => reveal(target));
    } else {
      observer.observe(target);
    }
  }
}

/**
 * Keyboard users can reach content before it scrolls far enough to trigger the
 * reveal, and a focus ring inside an `opacity: 0` element is invisible
 * (WCAG 2.4.7). Focus therefore reveals immediately.
 *
 * @param {FocusEvent} event
 */
function onFocusIn(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const wrapper = target.closest(`[${READY_ATTRIBUTE}]:not(.${VISIBLE_CLASS})`);
  if (wrapper) reveal(wrapper);
}

let watching = false;

/**
 * Re-scan on a DOM change, coalesced to one pass per frame.
 *
 * @param {Element} root
 */
function watch(root) {
  let pending = false;

  new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      claim(document);
      pending = false;
    });
  }).observe(root, { childList: true, subtree: true });
}

/**
 * Attached the first time this page actually claims something, so a shop with
 * the feature switched off pays for none of it.
 */
function startWatching() {
  if (watching) return;
  watching = true;

  document.addEventListener('focusin', onFocusIn);
  watch(document.body);
}


/*
 * Parallax.
 *
 * The layer is offset directly on scroll rather than through
 * `animation-timeline: view()`. That CSS route was written first and does not
 * render: on a real store page in Chrome 151 the property parses,
 * `CSS.supports('animation-timeline', 'view()')` returns true and a ViewTimeline
 * reports live progress, yet no transform is ever produced — reproduced on a
 * bare in-flow probe element too, so it is not the theme's markup. Writing the
 * transform here works in every browser and needs no support floor.
 *
 * A host is either a background wrapper or, on the blocks whose image is their
 * own content, the wrapper around that image. Both drift the same way: the
 * layer moves inside the host's frame while the frame scrolls normally.
 *
 * Only hosts currently on screen are updated, and only while the viewport is
 * wide enough, so the work stays proportional to what is visible.
 */

/** @type {Element[]} */
let parallaxHosts = [];

/** @type {Set<Element>} */
const visibleParallax = new Set();

/**
 * The element inside the host that actually drifts.
 *
 * @param {Element} host
 * @returns {HTMLElement | null}
 */
function parallaxLayer(host) {
  const layer = host.querySelector('.background-image-container, picture, img');
  return layer instanceof HTMLElement ? layer : null;
}

/**
 * Offsets one layer for the host's current position in the viewport.
 *
 * @param {Element} host
 */
function positionParallax(host) {
  const layer = parallaxLayer(host);
  if (!layer) return;

  const styles = getComputedStyle(host);
  const strength = parseFloat(styles.getPropertyValue('--sd-parallax-strength')) || 50;

  /*
   * Travel is a share of the layer's own height, so the drift is proportional at
   * any section height rather than only reading on tall ones. The overscale has
   * to grow with it: a shift of T% each way needs at least T% of spare image
   * beyond each edge, which is (scale - 1) / 2. Deriving both from the strength
   * keeps that guarantee instead of leaving a fixed scale to be outgrown — an
   * earlier fixed 1.12 capped travel at a barely visible 5%.
   */
  const travel = strength * 0.15;
  const scale = 1 + strength * 0.0035;
  const box = host.getBoundingClientRect();
  const raw = (window.innerHeight - box.top) / (window.innerHeight + box.height);
  const progress = Math.min(1, Math.max(0, raw));

  layer.style.transform = `translateY(${(progress * 2 - 1) * travel}%) scale(${scale})`;
}

function updateParallax() {
  // Read on every pass rather than once at startup, so a window resized across
  // the breakpoint stops writing transforms straight away.
  if (!mediaQueryLarge.matches) return;

  for (const host of visibleParallax) positionParallax(host);
}

/**
 * Returns every layer to its natural position and drops the compositor hint.
 *
 * Without this a layer stays frozen at whatever offset it last had when the
 * viewport narrows past the breakpoint — and on SD Banner that offset would sit
 * on the mobile image, which the effect is not meant to touch at all.
 */
function resetParallax() {
  for (const host of parallaxHosts) {
    const layer = parallaxLayer(host);
    if (!layer) continue;

    layer.style.transform = '';
    layer.style.willChange = '';
  }
}

/**
 * Desktop only, decided live rather than once at load: a window widened past
 * the breakpoint starts the effect and a narrowed one clears it. `will-change`
 * is stamped here too, so a phone never holds a compositor layer for an effect
 * it does not run.
 */
function onParallaxBreakpoint() {
  if (!mediaQueryLarge.matches) {
    resetParallax();
    return;
  }

  for (const host of parallaxHosts) {
    const layer = parallaxLayer(host);
    if (layer) layer.style.willChange = 'transform';
  }

  updateParallax();
}

function initParallax() {
  parallaxHosts = Array.from(document.querySelectorAll('[data-sd-parallax]'));
  if (parallaxHosts.length === 0) return;

  const seen = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) visibleParallax.add(entry.target);
      else visibleParallax.delete(entry.target);
    }
    updateParallax();
  });

  for (const host of parallaxHosts) seen.observe(host);

  let queued = false;
  const onScroll = () => {
    // Below the breakpoint there is nothing to position, so no frame is even
    // queued — a phone scrolling past a parallax host does no work at all.
    if (queued || !mediaQueryLarge.matches) return;
    queued = true;
    requestAnimationFrame(() => {
      updateParallax();
      queued = false;
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  mediaQueryLarge.addEventListener('change', onParallaxBreakpoint);

  onParallaxBreakpoint();
}

function init() {
  if (prefersReducedMotion()) return;

  claim(document);
  initParallax();

  // A theme-editor reload can introduce a wrapper where there was none. This is
  // one event listener and fires only in the editor, so it costs nothing live.
  document.addEventListener('shopify:section:load', () => claim(document));

  // The async hosts are watched individually rather than watching the whole
  // document, because they appear on pages that animate nothing at all.
  for (const host of document.querySelectorAll(ASYNC_HOSTS)) watch(host);
}

init();
